<template>
  <!-- Card 86akqxu0h: o formulário de pesquisa respondido dentro do Connect.
       Chega pelo botão da comunicação (aba Comunicados), pelo destaque da
       página inicial ou pelo aviso ao entrar. -->
  <div class="sa">
    <page-skeleton v-if="loading" variant="detail" class="mt-4" />

    <div v-else-if="loadError" class="sa-card sa-state">
      <v-icon size="40" color="#8a8499">mdi-file-question-outline</v-icon>
      <div class="sa-state__title">{{ $t(loadError) }}</div>
      <v-btn text :color="color" @click="goHome">{{
        $t("surveys.backHome")
      }}</v-btn>
    </div>

    <template v-else-if="survey">
      <div class="sa-card sa-head">
        <div class="sa-eyebrow">
          <v-icon size="16" :color="color">mdi-clipboard-text-outline</v-icon>
          {{ $t("surveys.eyebrow") }}
        </div>
        <h1 class="sa-title">{{ survey.title }}</h1>
        <p v-if="survey.description" class="sa-description">
          {{ survey.description }}
        </p>
        <div class="sa-meta">
          <span class="sa-status" :class="`sa-status--${status}`">
            {{ statusLabel }}
          </span>
          <span v-if="survey.anonymous" class="sa-chip">
            <v-icon size="14">mdi-incognito</v-icon>
            {{ $t("surveys.anonymous") }}
          </span>
          <span v-if="survey.closesAt && status === 'pending'" class="sa-chip">
            {{ $t("surveys.answerBy", { date: survey.closesAt.slice(0, 10) }) }}
          </span>
        </div>
      </div>

      <!-- Enviado agora -->
      <div v-if="confirmation" class="sa-card sa-state">
        <v-icon size="48" color="#1f9d55">mdi-check-circle-outline</v-icon>
        <div class="sa-state__title">{{ $t("surveys.sentTitle") }}</div>
        <p class="sa-state__text">
          {{ confirmation === true ? $t("surveys.thanks") : confirmation }}
        </p>
        <v-btn depressed rounded dark :color="color" @click="goHome">
          {{ $t("surveys.backHome") }}
        </v-btn>
      </div>

      <!-- Respondido antes -->
      <div v-else-if="status === 'answered'" class="sa-card sa-state">
        <v-icon size="44" color="#1f9d55">mdi-check-circle-outline</v-icon>
        <div class="sa-state__title">{{ $t("surveys.alreadyTitle") }}</div>
        <p class="sa-state__text">
          {{ $t("surveys.alreadyText", { date: survey.submittedAt }) }}
        </p>
        <v-btn text :color="color" @click="goHome">{{
          $t("surveys.backHome")
        }}</v-btn>
      </div>

      <!-- Encerrado -->
      <div v-else-if="status === 'closed'" class="sa-card sa-state">
        <v-icon size="44" color="#8a8499">mdi-lock-outline</v-icon>
        <div class="sa-state__title">{{ $t("surveys.closedTitle") }}</div>
        <p class="sa-state__text">
          {{ $t("surveys.closedText") }}
        </p>
        <v-btn text :color="color" @click="goHome">{{
          $t("surveys.backHome")
        }}</v-btn>
      </div>

      <!-- Pendente -->
      <template v-else>
        <div v-if="restoredAt" class="sa-note">
          <v-icon size="18" :color="color">mdi-history</v-icon>
          {{ $t("surveys.resumed", { date: restoredAt }) }}
        </div>

        <!-- Seção atual (formulário em páginas) -->
        <div v-if="paged" class="sa-card sa-section">
          <div class="sa-section__progress">
            <span>{{
              $t("surveys.page", {
                current: pageIndex + 1,
                total: pages.length,
              })
            }}</span>
            <v-progress-linear
              :value="((pageIndex + 1) / pages.length) * 100"
              :color="color"
              background-color="var(--dk-line, #ece8f3)"
              rounded
              height="6"
            />
          </div>
          <h2 v-if="currentPage.title" class="sa-section__title">
            {{ currentPage.title }}
          </h2>
          <p v-if="currentPage.description" class="sa-description mb-0">
            {{ currentPage.description }}
          </p>
        </div>

        <survey-questions
          v-model="answers"
          :questions="survey.questions"
          :page="currentPage.id"
          :errors="errors"
          :color="color"
          :disabled="busy"
        />

        <div class="sa-footer">
          <div v-if="errorCount" class="sa-footer__error">
            {{ $tc("surveys.toReview", errorCount, { count: errorCount }) }}
          </div>
          <div v-if="isLastPage" class="sa-footer__hint">
            {{ $t("surveys.checkBefore") }}
          </div>
          <div class="sa-actions">
            <v-btn
              v-if="pageIndex > 0"
              text
              :color="color"
              :disabled="busy"
              @click="previous"
            >
              <v-icon left>mdi-chevron-left</v-icon>
              {{ $t("common.back") }}
            </v-btn>
            <div class="sa-actions__end">
              <span v-if="savedAt" class="sa-saved">
                {{ $t("surveys.savedAt", { time: savedAt.slice(11, 16) }) }}
              </span>
              <v-btn
                outlined
                rounded
                :color="color"
                :loading="saving"
                :disabled="sending"
                @click="saveDraft()"
              >
                {{ $t("surveys.saveForLater") }}
              </v-btn>
              <v-btn
                v-if="!isLastPage"
                depressed
                rounded
                dark
                :color="color"
                :disabled="busy"
                @click="next"
              >
                {{ $t("surveys.next") }}
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn
                v-else
                depressed
                rounded
                dark
                :color="color"
                :loading="sending"
                :disabled="saving"
                @click="submit"
              >
                {{ $t("surveys.submit") }}
              </v-btn>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";
import pitchPartnerApi from "@/shared/pitchPartnerApi";
import { classThemes } from "@/shared/classThemes";
import {
  checkAnswers,
  surveyPages,
  visibleAnswers,
  visibleQuestionIds,
} from "@/shared/surveyLogic";
import SurveyQuestions from "./SurveyQuestions.vue";
import { dispatchSurveysRefresh } from "./surveyEvents";

// Salvamento automático do rascunho enquanto a pessoa responde (além do
// botão, da troca de página e da saída da tela).
const AUTOSAVE_MS = 30000;

const STATUS_LABELS = {
  pending: "surveys.pending",
  answered: "surveys.statusAnswered",
  closed: "surveys.statusClosed",
};

export default {
  name: "SurveyAnswerPage",

  components: { SurveyQuestions },

  props: {
    profile: {
      type: String,
      default: "customer",
      validator: (value) => ["customer", "partner"].includes(value),
    },
  },

  data() {
    return {
      survey: null,
      answers: {},
      errors: {},
      loading: false,
      loadError: "",
      sending: false,
      confirmation: "",
      // Página (seção) atual; null = formulário sem seções.
      pageId: null,
      // Rascunho. "dirty" = mudou desde o último salvamento.
      saving: false,
      savedAt: "",
      restoredAt: "",
      dirty: false,
      autosaveTimer: null,
    };
  },

  computed: {
    api() {
      return this.profile === "partner" ? pitchPartnerApi : pitchCustomerApi;
    },
    color() {
      return classThemes[this.profile]?.primary || "#A826E1";
    },
    status() {
      return this.survey?.status || "pending";
    },
    statusLabel() {
      if (this.confirmation) return this.$t(STATUS_LABELS.answered);
      const key = STATUS_LABELS[this.status];
      return key ? this.$t(key) : "";
    },
    errorCount() {
      return Object.keys(this.errors).length;
    },
    busy() {
      return this.sending || this.saving;
    },
    // Páginas com alguma pergunta visível para as respostas atuais: um
    // salto para uma seção adiante tira as do meio do caminho.
    pages() {
      return surveyPages(
        this.survey?.sections || [],
        this.survey?.questions || [],
        this.answers
      );
    },
    paged() {
      return (this.survey?.sections || []).length > 0;
    },
    pageIndex() {
      const index = this.pages.findIndex((page) => page.id === this.pageId);
      return index < 0 ? 0 : index;
    },
    currentPage() {
      return this.pages[this.pageIndex] || { id: null };
    },
    isLastPage() {
      return this.pageIndex >= this.pages.length - 1;
    },
  },

  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.load();
      },
    },
    // Corrigiu a pergunta: o aviso dela some.
    answers(next, previous) {
      if (!this.loading) this.dirty = true;
      if (!this.errorCount) return;
      const changed = Object.keys(this.errors).filter(
        (id) => next[id] !== (previous || {})[id]
      );
      if (!changed.length) return;
      const errors = { ...this.errors };
      changed.forEach((id) => delete errors[id]);
      this.errors = errors;
    },
  },

  mounted() {
    this.autosaveTimer = setInterval(
      () => this.saveDraft({ silent: true }),
      AUTOSAVE_MS
    );
  },

  beforeDestroy() {
    clearInterval(this.autosaveTimer);
  },

  // Saiu no meio do caminho: o que já foi preenchido fica salvo.
  beforeRouteLeave(to, from, next) {
    this.saveDraft({ silent: true });
    next();
  },

  methods: {
    async load() {
      const id = this.$route.params.id;
      if (!id) return;

      this.loading = true;
      this.loadError = "";
      this.confirmation = "";
      this.errors = {};
      this.answers = {};
      this.pageId = null;
      this.savedAt = "";
      this.restoredAt = "";

      try {
        const res = await this.api.getSurvey(id);
        this.survey = res.data?.data || null;
        if (!this.survey) this.loadError = "surveys.notFound";
        // Rascunho salvo: volta com as respostas e na página em que parou.
        const draft = this.survey?.draft;
        if (draft) {
          this.answers = { ...(draft.answers || {}) };
          this.pageId = draft.sectionId || null;
          this.savedAt = draft.savedAt || "";
          this.restoredAt = draft.savedAt || "";
        }
        if (this.paged && !this.pages.some((p) => p.id === this.pageId)) {
          this.pageId = this.pages[0]?.id ?? null;
        }
      } catch (err) {
        this.survey = null;
        this.loadError =
          err?.response?.status === 404
            ? "surveys.notFound"
            : "surveys.openFailed";
      } finally {
        // Depois do tick: o watcher de respostas ainda vê "loading" e não
        // marca o rascunho recém-carregado como alterado.
        this.$nextTick(() => {
          this.loading = false;
          this.dirty = false;
        });
      }
    },

    // Erros só das perguntas visíveis desta página.
    pageErrors() {
      const { errors } = checkAnswers(this.survey.questions, this.answers);
      if (!this.paged) return errors;
      const page = this.currentPage.id;
      return Object.keys(errors).reduce((acc, id) => {
        const question = this.survey.questions.find((item) => item.id === id);
        if (question?.sectionId === page) acc[id] = errors[id];
        return acc;
      }, {});
    },

    next() {
      const errors = this.pageErrors();
      this.errors = errors;
      if (Object.keys(errors).length) {
        this.scrollToFirstError();
        return;
      }
      const target = this.pages[this.pageIndex + 1];
      if (!target) return;
      this.pageId = target.id;
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.saveDraft({ silent: true });
    },

    previous() {
      const target = this.pages[this.pageIndex - 1];
      if (!target) return;
      this.errors = {};
      this.pageId = target.id;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    // "Salvar para continuar depois". Silencioso = automático (troca de
    // página, intervalo, saída da tela): só salva se mudou e não incomoda
    // se falhar.
    async saveDraft({ silent = false } = {}) {
      if (!this.survey || this.status !== "pending" || this.confirmation) {
        return;
      }
      if (silent && (!this.dirty || this.busy)) return;

      this.saving = true;
      this.dirty = false;
      try {
        const res = await this.api.saveSurveyDraft(
          this.survey.id,
          this.answers,
          this.paged ? this.currentPage.id : null
        );
        this.savedAt = res.data?.data?.savedAt || this.savedAt;
        dispatchSurveysRefresh();
        if (!silent) {
          this.$root.$emit("display-alert", {
            text: this.$t("surveys.draftSaved"),
            type: 0,
          });
        }
      } catch (err) {
        this.dirty = true;
        if (silent) return;
        const data = err?.response?.data || {};
        this.$root.$emit("display-alert", {
          text: data.message || this.$t("surveys.saveFailed"),
          type: 1,
        });
        // Já respondido ou encerrado em outra aba: mostra o estado atual.
        if (err?.response?.status === 422) this.load();
      } finally {
        this.saving = false;
      }
    },

    async submit() {
      const { errors } = checkAnswers(this.survey.questions, this.answers);
      this.errors = errors;
      if (Object.keys(errors).length) {
        this.goToFirstError();
        return;
      }

      this.sending = true;
      try {
        const res = await this.api.submitSurvey(
          this.survey.id,
          visibleAnswers(this.survey.questions, this.answers)
        );
        this.dirty = false;
        // Sem mensagem própria do formulário, `true` mostra o agradecimento
        // padrão no idioma da tela.
        this.confirmation = res.data?.confirmationMessage || true;
        this.survey = { ...this.survey, status: "answered" };
        dispatchSurveysRefresh();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        const data = err?.response?.data || {};
        if (data.errors) {
          this.errors = data.errors;
          this.goToFirstError();
        }
        this.$root.$emit("display-alert", {
          text: data.message || this.$t("surveys.submitFailed"),
          type: 1,
        });
        // Já respondido ou encerrado (em outra aba, por exemplo): mostra o
        // estado atual.
        if (err?.response?.status === 422 && !data.errors) this.load();
      } finally {
        this.sending = false;
      }
    },

    // No formulário em páginas, o erro pode estar numa página anterior: abre
    // a página da primeira pergunta com erro, na ordem em que aparecem.
    goToFirstError() {
      if (this.paged) {
        const visible = visibleQuestionIds(this.survey.questions, this.answers);
        const first = visible.find((id) => this.errors[id]);
        const question = this.survey.questions.find(
          (item) => item.id === first
        );
        if (question?.sectionId) this.pageId = question.sectionId;
      }
      this.scrollToFirstError();
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const first = this.$el.querySelector(".sq-item--error");
        if (first)
          first.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },

    goHome() {
      const target =
        this.profile === "partner"
          ? "/connect/professor/cockpit"
          : "/connect/aluno/cockpit";
      this.$router.push(target).catch(() => null);
    },
  },
};
</script>

<style scoped>
.sa {
  max-width: 760px;
  margin: 0 auto;
  padding: 8px 0 40px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.sa-center {
  display: flex;
  justify-content: center;
  padding: 60px 0;
}

.sa-card {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--dk-line, #ece8f3);
  border-radius: 16px;
  padding: 22px 24px;
}

.sa-eyebrow {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--dk-muted, #6b6580);
}

.sa-title {
  margin: 8px 0 6px;
  font-size: 1.5rem;
  line-height: 1.25;
  color: var(--dk-ink, #2b2540);
}

.sa-description {
  margin: 0 0 10px;
  color: var(--dk-ink-soft, #4d4760);
  white-space: pre-line;
}

.sa-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.sa-status,
.sa-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: var(--dk-paper, #f4f2f8);
  color: var(--dk-ink-soft, #4d4760);
}

.sa-status--pending {
  background: var(--dk-ember-wash, #fff6db);
  color: var(--dk-ember-deep, #8a6500);
}

.sa-status--answered {
  background: #e3f6ea;
  color: #1f7a45;
}

.sa-status--closed {
  background: #eeedf1;
  color: #5f5a6d;
}

.sa-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 36px 24px;
}

.sa-state__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--dk-ink, #2b2540);
}

.sa-state__text {
  margin: 0;
  color: var(--dk-ink-soft, #4d4760);
  max-width: 460px;
}

.sa-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.sa-footer__error {
  color: var(--dk-red, #e5484d);
  font-weight: 700;
  font-size: 0.875rem;
}

.sa-footer__hint {
  color: var(--dk-muted, #6b6580);
  font-size: 0.82rem;
  text-align: right;
}

.sa-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
}

.sa-actions__end {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-left: auto;
}

.sa-saved {
  color: var(--dk-muted, #6b6580);
  font-size: 0.8rem;
}

.sa-note {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--dk-paper, #f4f2f8);
  color: var(--dk-ink-soft, #4d4760);
  font-size: 0.875rem;
}

.sa-section__progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--dk-muted, #6b6580);
}

.sa-section__title {
  margin: 12px 0 4px;
  font-size: 1.15rem;
  color: var(--dk-ink, #2b2540);
}

@media (max-width: 600px) {
  .sa-card {
    padding: 18px 16px;
  }

  .sa-footer {
    align-items: stretch;
  }

  .sa-footer__hint {
    text-align: left;
  }

  .sa-actions,
  .sa-actions__end {
    flex-direction: column-reverse;
    align-items: stretch;
    margin-left: 0;
  }
}
</style>
